import { PaginationResult } from '@/models/pagination.model';
import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from './base.service';
import { User, UserLevel } from '@/models/user.model';
import { Co2Results } from '@/models/co2-quiz.model';
import { Auth, LoginData } from '@/models/login.model';

export interface FilterData {
  search: string;
  level: UserLevel | null;
  page: number;
  count: number;
  sort: string;
  direction: 'asc' | 'desc';
}

class UsersService extends BaseService {
  private url: string = '/api/user';
  private notificationUrl: string = '/api/notification';
  private emailUrl: string = '/api/email';
  private twoFactorUrl: string = '/api/2fa';

  constructor() {
    super();
  }

  public async get(id: number): Promise<DataResponse<User>> {
    const res = await this.$http.get<User>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getFull(id: number): Promise<DataResponse<User>> {
    const res = await this.$http.get<User>(`${this.url}/${id}/full`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getCo2Results(id: number): Promise<DataResponse<Co2Results[]>> {
    const res = await this.$http.get<Co2Results[]>(
      `${this.url}/${id}/co2-results`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getAll(): Promise<DataResponse<User[]>> {
    const res = await this.$http.get<User[]>(`${this.url}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getList(
    filters: FilterData,
  ): Promise<DataResponse<PaginationResult<User[]>>> {
    let query = `?page=${filters.page}&count=${filters.count}`;

    if (filters.search) {
      query = `${query}&search=${filters.search}`;
    }
    if (filters.sort) {
      query = `${query}&sort=${filters.sort}`;
    }
    if (filters.direction) {
      query = `${query}&direction=${filters.direction}`;
    }
    if (filters.level) {
      query = `${query}&level=${filters.level}`;
    }

    const res = await this.$http.get<PaginationResult<User[]>>(
      `${this.url}/list${query}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async create(user: Partial<User>): Promise<DataResponse<User>> {
    const res = await this.$http.post<User>(this.url, user);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
      message: res.message,
    };
  }

  public async edit(user: User): Promise<DataResponse<User>> {
    const update: Partial<User> = { ...user };

    // delete all the stuff that should not be updated here.
    delete update.deviceID;
    delete update.stories;
    delete update.actions;
    delete update.initiatives;
    delete update.infographics;
    delete update.steps;
    delete update.userCo2QuizAnswers;

    const res = await this.$http.put<User>(
      `${this.url}-admin/${user.id}`,
      update,
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
      message: res.message,
    };
  }

  public async resetBPT(id: number): Promise<DataResponse<User>> {
    const res = await this.$http.post<User>(`${this.url}/bpt/${id}`, {});

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async resetPassword(
    id: number,
    password: string,
  ): Promise<DataResponse<User>> {
    const res = await this.$http.put<User>(`${this.url}/reset-password`, {
      id,
      password,
    });

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async ResetCo2Quiz(id: number): Promise<DataResponse<User>> {
    const res = await this.$http.post<User>(`${this.url}/co2quiz/${id}`, {});

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }
  public async reset(id: number): Promise<DataResponse<User>> {
    const res = await this.$http.get<User>(`${this.url}/${id}/reset`);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async deleteData(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}/data`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserStep(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}-step/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserAction(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(
      `${this.url}-action/${id}`,
    );

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserInfoGraphic(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(
      `${this.url}-info-graphic/${id}`,
    );

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserCo2Answer(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(
      `${this.url}-co2-quiz-answer/${id}`,
    );

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserCo2Results(
    id: number,
    userId: number,
  ): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(
      `${this.url}-co2-results/${id}/${userId}`,
    );

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserInitiative(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(
      `${this.url}-initiative/${id}`,
    );

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async deleteUserStory(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}-story/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async sendNotification(notification: {
    userId: number;
    title: string;
    description: string;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.notificationUrl}/user`, {
      id: notification.userId,
      title: notification.title,
      body: notification.description,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendDiscountNotification(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.notificationUrl}/user/initiative/discount`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendPopularTestNotification(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`/api/popular-initiatives-notification/${notification.userId}`, {});

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendDiscountEmail(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.emailUrl}/user/initiative/discount`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendPopularNotification(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.notificationUrl}/user/initiative/popular`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendPopularEmail(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.emailUrl}/user/initiative/popular`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendPopularLearningItemNotification(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.notificationUrl}/user/learning-item/popular`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendPopularLearningItemEmail(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.emailUrl}/user/learning-item/popular`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendQuizNotification(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.notificationUrl}/user/quiz`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async sendQuizEmail(notification: {
    userId: number;
  }): Promise<{ status: boolean; data: { status: string; id: string } }> {
    const res = await this.$http.post<
      DataResponse<{ status: string; id: string }>
    >(`${this.emailUrl}/user/quiz`, {
      id: notification.userId,
    });

    return {
      status: res.status === Status.Created,
      data: res.data.data,
    };
  }

  public async enable2FA(): Promise<{
    qrCodeDataUrl: string;
    message: string;
  }> {
    const res = await this.$http.get<{
      qrCodeDataUrl: string;
      message: string;
    }>(`${this.twoFactorUrl}/generate`);

    return res.data;
  }

  public async enable2FACold(loginData: LoginData): Promise<{
    qrCodeDataUrl: string;
    message: string;
  }> {
    const res = await this.$http.post<{
      qrCodeDataUrl: string;
      message: string;
    }>(`${this.twoFactorUrl}/generate-cold`, loginData);

    return res.data;
  }

  public async verify2FA(token: string): Promise<{
    twoFactorEnabled: boolean;
    message: string;
  }> {
    const res = await this.$http.post<{
      twoFactorEnabled: boolean;
      message: string;
    }>(`${this.twoFactorUrl}/verify`, { token });

    return res.data;
  }

  public async verify2FACold(data: LoginData & { token: string }): Promise<{
    twoFactorEnabled: boolean;
    message: string;
  }> {
    const res = await this.$http.post<{
      twoFactorEnabled: boolean;
      message: string;
    }>(`${this.twoFactorUrl}/verify-cold`, data);

    return res.data;
  }

  public async validate2FA(data: {
    email: string;
    password: string;
    token: string;
  }): Promise<Auth> {
    const res = await this.$http.post<Auth>(
      `${this.twoFactorUrl}/validate`,
      data,
    );

    return res.data;
  }

  public async disable2FA(id: number): Promise<{
    twoFactorDisabled: boolean;
    message: string;
  }> {
    const res = await this.$http.post<{
      twoFactorDisabled: boolean;
      message: string;
    }>(`${this.twoFactorUrl}/disable`, { id });
    return res.data;
  }

  public async download(ids: string): Promise<void> {
    await this.$http.post(`${this.url}-csv`, { ids });
  }
}

export default new UsersService();
